import { useMemo, useState } from "react";
import { MiList } from "@miplatform/components";
import {
  BUTTON_LABELS,
  PAGINATION,
  PERMISSIONS,
  USER_ACTIVITY_STATUSES,
} from "@miplatform/constants";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IUserListItemResponse, IVerticalTabProps } from "@miplatform/types";
import { mdiCancel, mdiPencilOutline, mdiRefresh } from "@mdi/js";
import { AddButton, VerticalTabsLayout } from "@mibuildweb/components";
import { usePermissions, useUser, useUsers } from "@mibuildweb/hooks";
import { useTheme } from "@mui/material";
import { ConfigPageLayout } from "../ConfigPageLayout";
import { ConfigProfileCard } from "../components";

const userStatusTabs = [
  { label: "All", value: "All", filter: {} },
  {
    label: USER_ACTIVITY_STATUSES.ACTIVE,
    value: USER_ACTIVITY_STATUSES.ACTIVE,
    filter: {
      isDisabled: { eq: false },
      status: { eq: USER_ACTIVITY_STATUSES.ACTIVE },
    },
  },
  {
    label: "Inactive",
    value: "Inactive",
    filter: { isDisabled: { eq: true } },
  },
  {
    label: USER_ACTIVITY_STATUSES.PENDING,
    value: USER_ACTIVITY_STATUSES.PENDING,
    filter: {
      isDisabled: { eq: false },
      status: { eq: USER_ACTIVITY_STATUSES.PENDING },
    },
  },
];

export function ConfigUserList({ module }: Readonly<{ module?: string }>) {
  const { push } = useRouter();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const { hasPermission } = usePermissions();
  const canCreateUser = hasPermission(PERMISSIONS.USER.CREATE) && !module;
  const canUpdateUser = hasPermission(PERMISSIONS.USER.UPDATE);
  const canDeleteUser = hasPermission(PERMISSIONS.USER.DELETE) && !module;

  const {
    users,
    getUsersIncludeDisabled,
    setUsers,
    loading,
    recentUsers,
    setRecentUsers,
    filter,
    setFilter,
    resendInvitation,
    getRecentUsersIncludeDisabled,
  } = useUsers({ debouncedSearch, module });
  const { activateUser, deactivateUser } = useUser();

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleTabChange = (
    tab?: IVerticalTabProps<IUserListItemResponse[]> | null
  ) => {
    setFilter(tab?.filter || {});
    setSearch("");
  };

  const navigateToUserDetails = (userId: string) => {
    push(userId);
  };

  const navigateToAddUser = () => {
    push("add-user");
  };

  const refreshData = () => {
    setTriggerRefresh((prev) => !prev);
  };

  const refreshUsers = useMemo(() => {
    return { filter, triggerRefresh };
  }, [filter, module, triggerRefresh]);

  return (
    <ConfigPageLayout
      title="Users"
      loading={loading}
      titleActionComponent={
        canCreateUser && (
          <AddButton onClick={navigateToAddUser} label="Add User" />
        )
      }
      search={search}
      onHandleSearch={handleSearchChange}
      searchPlaceholder="Search"
      hideOnBack
    >
      <VerticalTabsLayout
        hideStatusIcons
        tabs={userStatusTabs}
        onTabChange={handleTabChange}
        hideNavigationButtons
      >
        <MiList
          viewType="card"
          data={users}
          dataProvider={getUsersIncludeDisabled}
          loading={loading}
          setData={setUsers}
          recentData={recentUsers}
          setRecentData={setRecentUsers}
          recentDataProvider={getRecentUsersIncludeDisabled}
          CardComponent={ConfigProfileListCard}
          cardProps={{
            navigateToUserDetails,
            resendInvitation,
            onClick: navigateToUserDetails,
            onDeactivate: async (user: IUserListItemResponse) => {
              await deactivateUser(user.id, { modifiedOn: user.modifiedOn });
              refreshData();
            },
            onActivate: async (user: IUserListItemResponse) => {
              await activateUser(user.id, { modifiedOn: user.modifiedOn });
              refreshData();
            },
            canUpdateUser,
            canDeleteUser,
          }}
          paginationMode={PAGINATION.SERVER}
          refresh={refreshUsers}
          entityName="Users"
          emptyListProps={{
            text: "No Users found",
          }}
          search={debouncedSearch}
        />
      </VerticalTabsLayout>
    </ConfigPageLayout>
  );
}

const ConfigProfileListCard = ({
  item: user,
  navigateToUserDetails = () => {},
  resendInvitation = () => {},
  onClick = () => {},
  onDeactivate = () => {},
  onActivate = () => {},
  canUpdateUser,
  canDeleteUser,
}: {
  item: IUserListItemResponse;
  navigateToUserDetails: (userId: string) => void;
  resendInvitation: (user: IUserListItemResponse) => void;
  onClick?: (userId: string) => void;
  onDeactivate?: (user: IUserListItemResponse) => void;
  onActivate?: (user: IUserListItemResponse) => void;
  canUpdateUser?: boolean;
  canDeleteUser?: boolean;
}) => {
  const {
    isDisabled,
    modifiedOn,
    firstName,
    lastName,
    status,
    id,
    displayName,
    createdOn,
  } = user;
  const fullName = `${firstName} ${lastName}`;
  const theme = useTheme();

  const showResendButton =
    !isDisabled && status === USER_ACTIVITY_STATUSES.PENDING;

  const actionButtons = showResendButton
    ? [
        {
          name: "Resend Invite",
          onClick: () => {
            resendInvitation(user);
          },
        },
      ]
    : [];

  const menuActions = [];

  const editAction = {
    label: BUTTON_LABELS.EDIT,
    action: () => {
      navigateToUserDetails(id);
    },
    icon: mdiPencilOutline,
  };

  const deactivateAction = {
    label: BUTTON_LABELS.DEACTIVATE,
    action: () => {
      onDeactivate(user);
    },
    disabled: false,
    icon: mdiCancel,
    color: theme.palette.error.main,
  };

  const activateAction = {
    label: BUTTON_LABELS.ACTIVATE,
    action: () => {
      onActivate(user);
    },
    disabled: false,
    icon: mdiRefresh,
    color: theme.palette.success.main,
  };

  if (canUpdateUser) {
    menuActions.push(editAction);
  }
  if (canDeleteUser && !isDisabled) {
    menuActions.push(deactivateAction);
  }
  if (canDeleteUser && isDisabled) {
    menuActions.push(activateAction);
  }

  return (
    <ConfigProfileCard
      name={displayName ?? fullName}
      isActive={!isDisabled}
      modifiedOn={modifiedOn}
      actionButtons={actionButtons}
      menuActions={menuActions}
      createdOn={createdOn}
      onClick={() => onClick(id)}
    />
  );
};
